import { getApiUrl } from '../../../config/api';
import { handleAPIError } from '../../../services/apiErrorHandler';
import { BatchProcessOptions, DraftPrompt } from '../types/batch';

export const processBatchPrompts = async (
  projectId: string,
  draftPrompts: DraftPrompt[],
  options: BatchProcessOptions,
  onStatusUpdate: (type: string, status: 'processing' | 'completed' | 'error', message: string, promptId?: string, promptTitle?: string) => void
): Promise<void> => {
  const authToken = localStorage.getItem('authToken');

  // Group draft prompts by type
  const promptsByType = groupPromptsByType(draftPrompts, options.types);

  // Calculate total prompts to process
  const totalPrompts = Object.values(promptsByType).reduce((sum, prompts) => sum + prompts.length, 0);
  let processedCount = 0;

  console.log('🚀 Starting batch processing:', {
    totalPrompts,
    selectedTypes: options.types,
    promptsByType: Object.keys(promptsByType).map(type => ({
      type,
      count: promptsByType[type]?.length || 0
    }))
  });

  // Process each type
  for (const type of options.types) {
    const typePrompts = promptsByType[type] || [];
    const typeOptions = options[type as keyof BatchProcessOptions];
    
    console.log(`📦 Processing type: ${type}`, {
      promptCount: typePrompts.length,
      hasOptions: !!typeOptions
    });
    
    if (!typeOptions || typeof typeOptions === 'string' || Array.isArray(typeOptions)) {
      console.log(`⏭️ Skipping type ${type} - invalid options`);
      continue;
    }

    // Process each draft prompt of this type
    for (const prompt of typePrompts) {
      try {
        processedCount++;
        const promptId = prompt.id || prompt._id; // Use id first, fallback to _id
        const promptTitle = prompt.title || `${type} prompt`;
        
        // Show progress: "Processing 1 of 5"
        onStatusUpdate(type, 'processing', `Processing ${processedCount} of ${totalPrompts}...`, promptId, promptTitle);

        const attributes = `${type}-${typeOptions.style}-${typeOptions.purpose}`;

        // Get userIdea from nested userSelections or fallback to top-level
        const userIdea = prompt.userSelections?.userIdea || prompt.userIdea || '';

        // Debug: Log the prompt object to see what fields it has
        console.log('📋 Processing prompt:', {
          id: promptId,
          title: prompt.title,
          hasUserSelections: !!prompt.userSelections,
          hasUserIdea: !!userIdea,
          userIdea: userIdea,
          allKeys: Object.keys(prompt)
        });

        const requestBody = {
          projectId,
          promptId: promptId,  // Include the draft prompt ID to update
          userIdea: userIdea,  // Use the original user idea from nested structure
          attributes,
          mode: 'quick'
        };

        console.log('🔄 Batch processing request:', {
          promptId: promptId,
          projectId,
          attributes,
          hasUserIdea: !!userIdea,
          userIdeaLength: userIdea?.length
        });
        

        const response = await fetch(getApiUrl('/prompt-generator/generate'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          await handleAPIError(response);
        }

        const result = await response.json();

        console.log('✅ Batch processing response:', {
          success: result.success,
          promptId: promptId,
          hasGeneratedPrompt: !!result.generatedPrompt
        });

        if (result.success) {
          onStatusUpdate(type, 'completed', `✓ Generated successfully`, promptId, promptTitle);
        } else {
          throw new Error(result.message || 'Failed to generate');
        }

        // Small delay between requests to avoid overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        const promptId = prompt.id || prompt._id;
        console.error(`❌ Error processing ${type} prompt ${promptId}:`, error);
        const promptTitle = prompt.title || `${type} prompt`;
        onStatusUpdate(type, 'error', `✗ Failed to generate`, promptId, promptTitle);
      }
    }
    
    console.log(`✅ Completed processing type: ${type}`, {
      processed: typePrompts.length,
      totalProcessedSoFar: processedCount,
      totalPrompts
    });
  }
  
  console.log('🎉 Batch processing completed!', {
    totalProcessed: processedCount,
    totalPrompts
  });
};

// Helper function to determine prompt type based on title (for DRAFTS only)
// Uses same logic as PromptFilterService - checks first word of title
const getPromptType = (prompt: DraftPrompt): string => {
  const title = (prompt.title || '').toLowerCase();
  const firstWord = title.split(' ')[0];
  
  if (firstWord === 'music') {
    return 'music';
  }
  if (firstWord === 'image') {
    return 'image';
  }
  if (firstWord === 'narration' || firstWord === 'voice') {
    return 'narration';
  }
  
  // Others = anything that doesn't match known types
  if (!['image', 'video', 'music', 'sound', 'sfx', 'narration', 'voice', 'character'].includes(firstWord)) {
    return 'others';
  }
  
  return 'others';
};

// Group prompts by their type - ONLY INCLUDE DRAFTS
const groupPromptsByType = (
  prompts: DraftPrompt[],
  selectedTypes: string[]
): Record<string, DraftPrompt[]> => {
  const grouped: Record<string, DraftPrompt[]> = {};
  
  for (const prompt of prompts) {
    // CRITICAL: Only process draft prompts (not already generated)
    const isDraft = !prompt.generatedPrompt || prompt.generatedPrompt.trim() === '';
    if (!isDraft) continue;
    
    const type = getPromptType(prompt);
    if (selectedTypes.includes(type)) {
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(prompt);
    }
  }
  
  return grouped;
};
